/**
 * Parser for the OAuth social-callback URL, shared by the web callback route
 * (`routes/_auth/social-callback.tsx`, in-page `#…` redirect) and the native
 * deep-link flow (Phase 4). The server returns the signed-in user to
 * `https://app-<env>/auth/social/callback#status=ok&token=…&redirect=…`; the token
 * and status ride the URL FRAGMENT so they never hit a server access log, with a
 * QUERY fallback in case a platform strips the fragment on the way to the app.
 *
 * Pure + framework-free so the token-extraction seam is unit-tested in isolation.
 */

/** The path the social callback targets (both web and the App/Universal Link). */
export const SOCIAL_CALLBACK_PATH = "/auth/social/callback";

interface SocialCallbackParams {
  /** `ok` on success, an error status otherwise, `undefined` if absent. */
  status?: string;
  /** Opaque session token (Tauri Bearer). Only present on success. */
  token?: string;
  /** Safe redirect target (relative path). */
  redirect?: string;
  /** Provider/flow error code when `status !== "ok"`. */
  error?: string;
}

/**
 * Parse a `#…`/`?…` param string (leading `#`/`?` optional) into the callback
 * fields, percent-decoding values.
 */
export function parseSocialCallbackParams(raw: string): SocialCallbackParams {
  const clean = raw.replace(/^[#?]/, "");
  const out: SocialCallbackParams = {};
  if (clean === "") {
    return out;
  }
  for (const part of clean.split("&")) {
    const [k, v] = part.split("=");
    if (!k) {
      continue;
    }
    const key = decodeURIComponent(k);
    const value = decodeURIComponent((v ?? "").replaceAll("+", " "));
    if (key === "status" || key === "token" || key === "redirect" || key === "error") {
      out[key] = value;
    }
  }
  return out;
}

/**
 * Parse a FULL callback URL. Returns `null` when the URL is not the social
 * callback path, so unrelated deep links are ignored. Prefers the fragment;
 * falls back to the query string.
 */
export function parseSocialCallbackUrl(url: string): SocialCallbackParams | null {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return null;
  }
  if (parsed.pathname !== SOCIAL_CALLBACK_PATH) {
    return null;
  }
  const fragment = parsed.hash.replace(/^#/, "");
  const raw = fragment !== "" ? fragment : parsed.search.replace(/^\?/, "");
  return parseSocialCallbackParams(raw);
}
